import styles from "./mortgage.module.css"
import cx from "classnames"

import React, {useEffect, useRef} from "react"
import assign from "object-assign"
import {ThemeContext} from "activities/context"
import * as fonts from "shared/fonts"
import * as util from "activities/util"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import * as callouts from "activities/components/callouts"
import BarChart from "activities/components/charts/bar"
import AccessibilityTable from "activities/components/shared/accessibility-table"
import Carrot from "../../../images/carrot.svg?react"

export const calculatePayment = (price, payment, term, rate, insurance, tax, pmi, doIncludeExtras = false) => {
  const loanAmount = util.getLoanAmt(price, payment)
  let loanAmt = loanAmount
  const monthlyPayment = util.getMonthlyPayment(loanAmt, term, rate)
  let balance = loanAmt
  let totalInterest = 0
  const amortization = []
  for (let y = 0; y < term; y++) {
    const year = y + 1
    let interest = 0 // Interest payment for year y
    loanAmt = 0 // Principal payment for year y
    for (let m = 0; m < 12; m++) {
      const interestM = balance * util.getMonthlyRate(rate) // Interest payment for month m
      const loanAmtM = monthlyPayment - interestM // Principal payment for month m
      interest += interestM
      loanAmt += loanAmtM
      balance -= loanAmtM
    }
    // ES6: Object creation shorthand
    totalInterest += interest
    amortization.push({year, loanAmt, interest})
  }
  const extras = doIncludeExtras ? insurance / 12 + tax / 12 + (pmi * util.getLoanAmt(price, payment)) / 12 : 0
  return {
    loanAmount,
    monthlyPayment: monthlyPayment + extras,
    amortization,
    totalInterest,
    extras
  }
}

export default shared.ActivityFactory(
  ({getProps, getState, updateState}) => {
    const {price, payment, term, rate, insurance, tax, pmi, advancedOptions, prevAdvancedOpts, resetKey} = getState()
    const {id, theme} = getProps()
    const {loanAmount, monthlyPayment, amortization, totalInterest, extras} = calculatePayment(price, payment, term, rate / 100, insurance, tax, pmi / 100, advancedOptions)
    const isDisabled = price <= payment

    useEffect(() => {
      if (price < payment) updateState("payment")("0")
    }, [price])

    const mortgageCalculatorData = {
      legend: {
        x: {
          label: "Years",
          key: "year"
        },
        y: {
          label: "Annual Payments",
          type: "currency"
        }
      },
      fields: {
        year: {type: "number", tooltip: {title: "Year", order: 0}},
        loanAmt: {type: "currency", barOrder: 1, tooltip: {title: "Principal", order: 2, color: "color-1"}},
        interest: {type: "currency", barOrder: 2, tooltip: {title: "Interest", order: 1, color: "color-4"}}
      },
      values: {
        x: util.xAxisArray(amortization.length),
        y: amortization
      }
    }

    if (advancedOptions) {
      const fields = mortgageCalculatorData.fields
      delete fields["year"].tooltip
      fields["extras"] = {
        type: "currency",
        barOrder: 0,
        tooltip: {title: "Tax/Insurance", order: 3, color: "color-1"}
      }
      fields["loanAmt"].tooltip.color = "color-4"
      fields["interest"].tooltip.color = "color-2"
      mortgageCalculatorData.values.y = amortization.map((yearObj) => assign({extras: extras * 12}, yearObj))
    }

    const scrollDownRef = useRef()
    const scrollUpRef = useRef()

    useEffect(() => {
      if (advancedOptions) {
        scrollDownRef.current.lastElementChild.scrollIntoView({behavior: "smooth", block: "nearest"})
        updateState("prevAdvancedOpts")(true)
      } else if (prevAdvancedOpts === true && advancedOptions === false) {
        scrollUpRef.current.firstElementChild.firstElementChild.scrollIntoView({behavior: "smooth", block: "nearest"})
        updateState("prevAdvancedOpts")(false)
      }
    }, [advancedOptions])

    const accessibilityTableHeadings = [
      {
        name: "Years",
        key: "year"
      },
      {
        name: "Principal",
        key: "loanAmt"
      },
      {
        name: "Interest",
        key: "interest"
      }
    ]

    const accessibilityTableData = amortization.map((row) => ({
      year: row.year,
      loanAmt: util.formatDollarsNoCents(row.loanAmt),
      interest: util.formatDollarsNoCents(row.interest)
    }))

    const accessibilityTableLabel = "Mortgage Summary"

    return (
      <div>
        <shared.SummaryCalcContainer>
          <ThemeContext.Consumer>
            {(theme) => (
              <div ref={scrollUpRef}>
                <shared.InputsContainer className={cx(styles["input-container"], advancedOptions && styles["open"])}>
                  <inputs.CalcInput id={id} title="Home Price" onChange={updateState("price", util.fromString)} initialValue={price} resetKey={resetKey} />
                  <inputs.RangeInput
                    inputCls={styles["down-payment"]}
                    id={id}
                    title="Down Payment"
                    onChange={updateState("payment", (v) => util.fromString(price * (v / 100).toFixed(2)))}
                    initialValue={Math.round((payment / price) * 100) || "0"}
                    secondaryValue={payment}
                    min={0}
                    max={80}
                    step={1}
                    units="%"
                    valueType="percent"
                    disabled={isDisabled}
                    resetKey={resetKey}
                    color={theme["color-1"]}
                  />
                  <inputs.RangeInput
                    id={id}
                    title="Loan Term"
                    onChange={updateState("term", util.fromString)}
                    initialValue={term}
                    min={5}
                    max={30}
                    step={5}
                    units="years"
                    valueType="years-full"
                    disabled={isDisabled}
                    resetKey={resetKey}
                    color={theme["color-1"]}
                  />
                  <inputs.RangeInput
                    id={id}
                    title="Interest Rate"
                    onChange={updateState("rate", parseFloat)}
                    initialValue={rate}
                    min={1.5}
                    max={8}
                    step={0.05}
                    units="%"
                    valueType="percent"
                    disabled={isDisabled}
                    resetKey={resetKey}
                    color={theme["color-1"]}
                  />
                  <div ref={scrollDownRef} className={styles["advanced-options"]}>
                    <button onClick={() => updateState("advancedOptions")(!advancedOptions)} className={cx(styles["expand"], fonts.getFontClass())}>
                      <Carrot className={advancedOptions ? styles["up"] : styles["down"]} /> Advanced Options
                    </button>
                    <div className={styles["inputs"]}>
                      <inputs.SmallCalcInputWithTitle
                        id={id}
                        title="Home Insurance / Yr"
                        includeIcon={false}
                        onChange={updateState("insurance", util.fromString)}
                        initialValue={insurance}
                        resetKey={resetKey}
                      />
                      <inputs.SmallCalcInputWithTitle
                        id={id}
                        title="Property Tax / Yr"
                        includeIcon={false}
                        onChange={updateState("tax", util.fromString)}
                        initialValue={tax}
                        resetKey={resetKey}
                      />
                      <inputs.RangeInput
                        id={id}
                        title="PMI"
                        onChange={updateState("pmi", parseFloat)}
                        initialValue={pmi}
                        min={0}
                        max={20}
                        step={0.05}
                        units="%"
                        valueType="percent"
                        disabled={isDisabled}
                        resetKey={resetKey}
                        color={theme["color-1"]}
                      />
                    </div>
                  </div>
                </shared.InputsContainer>
              </div>
            )}
          </ThemeContext.Consumer>
          <shared.ChartContainer>
            <BarChart
              data={mortgageCalculatorData}
              disabled={isDisabled}
              theme={theme}
              aria-label="This bar graph visually represents how much interest and principal on your mortgage remains after each year. Read below for the loan amount, monthly payment amount, total interest to be paid, and pay-off date."
              data-testid="mortgage-chart"
            />
          </shared.ChartContainer>
          <shared.SummaryContainer className={styles["summary"]}>
            <callouts.Callout className={styles["callout"]}>
              <callouts.Detail label="Loan Amount" result={util.formatDollarsNoCents(util.ifNegative(loanAmount))} dataTestId="loan-amount" />
              <callouts.Detail label="Monthly Payment" result={util.formatDollarsNoCents(util.ifNegative(monthlyPayment))} dataTestId="monthly-payment" />
              <callouts.Detail label="Interest to be paid" result={util.formatDollarsNoCents(util.ifNegative(totalInterest))} dataTestId="interest" />
              <callouts.Detail label="Payoff Date" result={util.getPayoffDate(term)} dataTestId="payoff" />
            </callouts.Callout>
          </shared.SummaryContainer>
        </shared.SummaryCalcContainer>

        <AccessibilityTable headings={accessibilityTableHeadings} data={accessibilityTableData} label={accessibilityTableLabel} />
      </div>
    )
  },
  {
    stateFilter: (state) => {
      const {price, payment, term, rate, insurance, tax, pmi, advancedOptions} = state
      const {loanAmount, monthlyPayment, totalInterest} = calculatePayment(price, payment, term, rate, insurance, tax, pmi, advancedOptions)
      return assign(state, {
        loanAmount,
        monthlyPayment: util.round(monthlyPayment),
        totalInterest: util.round(totalInterest),
        payoffDate: util.getPayoffDate(term)
      })
    },
    defaultProps: {
      id: "mortgage-calculator",
      className: "mortgage-calculator",
      title: "Mortgage Calculator",
      subtitle: "Adjust the inputs to see how much your mortgage would cost over time."
    },
    defaultState: {
      price: 250000,
      payment: 50000,
      term: 30,
      rate: 6.5,
      insurance: 1000,
      tax: 2400,
      pmi: 0.5,
      advancedOptions: false,
      prevAdvancedOpts: null
    }
  }
)
